/**
 * Hook para gerenciamento de testes de carga
 * Sistema de stress test com métricas em tempo real
 */
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

// Interfaces

/**
 * Configuração do teste de carga
 * Suporta modo normal (mensagens genéricas) e modo fluxo (simulação de interação)
 */
export interface StressTestConfig {
  tenant_id: string;
  inbox_id: string;
  messages_per_second: number;
  duration_seconds: number;
  ramp_up_seconds?: number;
  message_types?: { text: number; image: number; audio: number };
  // Configurações de teste de fluxo
  flow_mode_enabled?: boolean;
  flow_id?: string;
  trigger_keyword?: string;
  response_interval_min_ms?: number;
  response_interval_max_ms?: number;
  max_interactions_per_flow?: number;
}

export interface StressTestRun {
  id: string;
  started_at: string;
  ended_at: string | null;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  config: StressTestConfig;
  results: Record<string, unknown> | null;
  tenant_id: string;
  inbox_id: string;
}

export interface StressTestMetric {
  id: number;
  run_id: string;
  timestamp: string;
  second_offset: number;
  messages_sent: number;
  messages_processed: number;
  messages_failed: number;
  queue_size: number;
  latency_avg_ms: number | null;
}

export interface StressTestStats {
  total_sent: number;
  total_processed: number;
  total_failed: number;
  avg_latency_ms: number;
  current_queue_size: number;
  throughput_per_second: number;
  error_rate: number;
}

export function useStressTestRuns(limit = 10) {
  return useQuery({
    queryKey: ['stress-test-runs', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stress_test_runs')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data as unknown as StressTestRun[];
    },
    refetchInterval: 5000,
  });
}

export function useStressTestDetails(testId: string | null) {
  return useQuery({
    queryKey: ['stress-test-details', testId],
    queryFn: async () => {
      if (!testId) return null;
      const { data, error } = await supabase.functions.invoke('stress-test-controller', {
        body: { action: 'status', test_id: testId },
      });
      if (error) throw error;
      return data as { test: StressTestRun; stats: StressTestStats | null; metrics: StressTestMetric[] };
    },
    enabled: !!testId,
    refetchInterval: testId ? 2000 : false,
  });
}

export function useStressTestMetricsRealtime(runId: string | null) {
  const [metrics, setMetrics] = useState<StressTestMetric[]>([]);

  useEffect(() => {
    if (!runId) { setMetrics([]); return; }
    const loadInitial = async () => {
      const { data } = await supabase.from('stress_test_metrics').select('*').eq('run_id', runId).order('timestamp', { ascending: true });
      if (data) setMetrics(data as unknown as StressTestMetric[]);
    };
    loadInitial();
    const channel = supabase.channel(`stress-metrics-${runId}`).on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'stress_test_metrics', filter: `run_id=eq.${runId}` }, (payload) => setMetrics(prev => [...prev, payload.new as StressTestMetric])).subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [runId]);

  return metrics;
}

export function useStressTestController() {
  const queryClient = useQueryClient();

  const startTest = useMutation({
    mutationFn: async (config: StressTestConfig) => {
      logger.info('StressTest', 'Iniciando teste', config);
      const { data, error } = await supabase.functions.invoke('stress-test-controller', { body: { action: 'start', config } });
      if (error) throw error;
      if (!data.success) throw new Error(data.error);
      return data as { success: boolean; test_id: string };
    },
    onSuccess: () => { toast.success('Teste iniciado'); queryClient.invalidateQueries({ queryKey: ['stress-test-runs'] }); },
    onError: (err: Error) => { toast.error(`Erro: ${err.message}`); },
  });

  const stopTest = useMutation({
    mutationFn: async (testId: string) => {
      const { data, error } = await supabase.functions.invoke('stress-test-controller', { body: { action: 'stop', test_id: testId } });
      if (error) throw error;
      return data;
    },
    onSuccess: () => { toast.success('Teste cancelado'); queryClient.invalidateQueries({ queryKey: ['stress-test-runs'] }); },
    onError: (err: Error) => { toast.error(`Erro: ${err.message}`); },
  });

  const generateContacts = useMutation({
    mutationFn: async (params: { tenant_id: string; count?: number }) => {
      const { data, error } = await supabase.functions.invoke('stress-test-controller', { body: { action: 'generate-contacts', ...params } });
      if (error) throw error;
      return data as { success: boolean; contacts_generated: number };
    },
    onSuccess: (data) => { toast.success(`${data.contacts_generated} contatos gerados`); },
    onError: (err: Error) => { toast.error(`Erro: ${err.message}`); },
  });

  const cleanup = useMutation({
    mutationFn: async (params: { tenant_id: string; cleanup_type?: 'all' | 'contacts' | 'tests' }) => {
      const { data, error } = await supabase.functions.invoke('stress-test-controller', { body: { action: 'cleanup', ...params } });
      if (error) throw error;
      return data as { success: boolean; contacts_deleted: number; tests_deleted: number };
    },
    onSuccess: (data) => { toast.success(`Limpeza: ${data.contacts_deleted} contatos`); queryClient.invalidateQueries({ queryKey: ['stress-test-runs'] }); },
    onError: (err: Error) => { toast.error(`Erro: ${err.message}`); },
  });

  // Mutation para resetar dados de teste de uma inbox específica
  const resetInbox = useMutation({
    mutationFn: async (inboxId: string) => {
      logger.info('StressTest', 'Resetando inbox', { inbox_id: inboxId });
      const { data, error } = await supabase.functions.invoke('stress-test-controller', { 
        body: { action: 'reset-inbox', inbox_id: inboxId } 
      });
      if (error) throw error;
      if (!data.success) throw new Error(data.error);
      return data as { 
        success: boolean; 
        messages_deleted: number; 
        conversations_deleted: number;
        flow_executions_deleted: number;
        flow_steps_deleted: number;
        flow_queue_deleted: number;
      };
    },
    onSuccess: (data) => { 
      toast.success(`Reset: ${data.messages_deleted} msgs, ${data.conversations_deleted} conversas, ${data.flow_executions_deleted} execuções`); 
      queryClient.invalidateQueries({ queryKey: ['stress-test-runs'] });
    },
    onError: (err: Error) => { toast.error(`Erro: ${err.message}`); },
  });

  return { startTest, stopTest, generateContacts, cleanup, resetInbox };
}

export function useQueueSize() {
  return useQuery({
    queryKey: ['queue-size'],
    queryFn: async () => {
      const { count, error } = await supabase.from('flow_queue').select('*', { count: 'exact', head: true }).eq('status', 'pending');
      if (error) throw error;
      return count || 0;
    },
    refetchInterval: 2000,
  });
}
